import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { graphqlQuery } from '../utils/graphqlApi';
import { listRunbooks } from '../graphql/queries';
import useUserPermissions from '../hooks/useUserPermissions';
import config from '../config/appConfig';

function RunbookList() {
  const navigate = useNavigate();
  const { canModify } = useUserPermissions();
  const [runbooks, setRunbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [selectedTribe, setSelectedTribe] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const fetchRunbooks = async (tribe) => {
    try {
      setLoading(true);
      setError(null);

      let allRunbooks = [];
      let nextToken = null;

      // Build query variables, include tribe filter if selected
      const variables = {};
      if (tribe) {
        variables.tribe = tribe;
      }

      // Paginate through all results from AppSync
      do {
        const result = await graphqlQuery(listRunbooks, { ...variables, nextToken });
        const connection = result.listRunbooks;
        if (connection.items) {
          allRunbooks = [...allRunbooks, ...connection.items];
        }
        nextToken = connection.nextToken;
      } while (nextToken);

      // Filter out runbooks with only 1 or 0 steps (not real runbooks)
      const filteredRunbooks = allRunbooks.filter(
        (rb) => rb.runbook_steps && rb.runbook_steps.length > 1
      );
      setRunbooks(filteredRunbooks);
    } catch (error) {
      console.error('Could not fetch runbooks:', error);
      setError(error.message || 'Failed to fetch runbooks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRunbooks(selectedTribe);
  }, [selectedTribe]);

  const handleTribeFilter = (tribe) => {
    setSelectedTribe(tribe);
    setCurrentPage(1);
    setSelectedKeyword(null);
  };

  const handleKeywordSearch = (value) => {
    setSearchKeyword(value);
    setCurrentPage(1);
    setSelectedKeyword(null);
  };

  const handleCheckboxChange = (keyword) => {
    setSelectedKeyword((prev) => (prev === keyword ? null : keyword));
  };

  const handleEditRunbook = () => {
    if (selectedKeyword) {
      navigate(`/edit-runbook/${encodeURIComponent(selectedKeyword)}`);
    }
  };

  const handleViewRunbook = (e, keyword) => {
    e.preventDefault();
    navigate(`/view-runbook/${encodeURIComponent(keyword)}`);
  };

  const formatModifiedAt = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
  };

  const filteredRunbooks = runbooks.filter((runbook) => {
    if (!searchKeyword.trim()) return true;
    return (runbook.keyword || '').toLowerCase().includes(searchKeyword.trim().toLowerCase());
  });

  const totalPages = Math.ceil(filteredRunbooks.length / rowsPerPage);
  const currentRunbooks = filteredRunbooks.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading runbooks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <p>{error}</p>
          <button
            className="kone-btn kone-btn-primary"
            onClick={() => fetchRunbooks(selectedTribe)}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="kone-section-title">Runbooks</h2>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="runbookKeywordSearch" className="kone-label mb-0">
              Search Keyword:
            </label>
            <input
              id="runbookKeywordSearch"
              type="text"
              className="kone-input"
              style={{ width: '250px' }}
              placeholder="Type keyword..."
              value={searchKeyword}
              onChange={(e) => handleKeywordSearch(e.target.value)}
            />
          </div>

          <div className="d-flex align-items-center gap-2">
            <label className="kone-label mb-0">Filter by Tribe:</label>
            <select
              className="kone-select"
              style={{ width: '220px' }}
              value={selectedTribe}
              onChange={(e) => handleTribeFilter(e.target.value)}
            >
              <option value="">All Tribes</option>
              {config.tribes.map((tribe) => (
                <option key={tribe} value={tribe}>
                  {tribe}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-hover">
          <thead>
            <tr>
              {canModify() && <th style={{ width: '50px' }}></th>}
              <th>Keyword</th>
              <th>Tribe</th>
              <th>Modified By</th>
              <th>Modified At</th>
              {/* <th>Is Automation</th> */}
              <th>Steps</th>
            </tr>
          </thead>
          <tbody>
            {currentRunbooks.length > 0 ? (
              currentRunbooks.map((runbook) => (
                <tr
                  key={runbook.keyword}
                  className={selectedKeyword === runbook.keyword ? 'table-active' : ''}
                >
                  {canModify() && (
                    <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedKeyword === runbook.keyword}
                        onChange={() => handleCheckboxChange(runbook.keyword)}
                      />
                    </td>
                  )}
                  <td style={{ verticalAlign: 'middle' }}>
                    <a
                      href={`/view-runbook/${encodeURIComponent(runbook.keyword)}`}
                      onClick={(e) => handleViewRunbook(e, runbook.keyword)}
                      style={{ color: '#1C64F2', textDecoration: 'none', fontWeight: 500 }}
                    >
                      {runbook.keyword}
                    </a>
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>{runbook.tribe}</td>
                  <td style={{ verticalAlign: 'middle' }}>{runbook.modified_by || '-'}</td>
                  <td style={{ verticalAlign: 'middle' }}>
                    {formatModifiedAt(runbook.modified_at)}
                  </td>
                  {/* <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                        {runbook.is_automation ? 'Yes' : 'No'}
                                    </td> */}
                  <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                    {runbook.runbook_steps?.length || 0}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={canModify() ? '7' : '6'} className="text-center py-4">
                  <div className="text-muted">
                    <p>No runbooks found.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <nav aria-label="Runbook pagination">
          <div className="d-flex justify-content-center align-items-center mt-4">
            <button
              className="kone-btn kone-btn-outline me-2"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="mx-3">
              Page {currentPage} of {totalPages} ({filteredRunbooks.length} matching runbooks)
            </span>
            <button
              className="kone-btn kone-btn-outline ms-2"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </nav>
      )}

      {selectedKeyword && canModify() && (
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            onClick={handleEditRunbook}
            className="kone-btn kone-btn-primary"
            title={`Edit runbook ${selectedKeyword}`}
          >
            Edit Selected
          </button>
        </div>
      )}
    </div>
  );
}

export default RunbookList;
